import React, { useState } from 'react';
import api from '../api';

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
const CANADIAN_PROVINCES = ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'];

const inp = { width: '100%', padding: '0.6rem', background: 'var(--dark)', border: '1px solid var(--border)', borderRadius: '2px', color: 'var(--text)', fontFamily: 'Crimson Text, serif', fontSize: '1rem', boxSizing: 'border-box' };
const lbl = { display: 'block', marginBottom: '0.4rem', color: 'var(--text-muted)', fontFamily: 'Cinzel, serif', fontSize: '0.8rem', letterSpacing: '0.05em' };
const section = { background: 'var(--dark-2)', border: '1px solid var(--border)', borderRadius: '4px', padding: '1.5rem', marginBottom: '1.5rem' };
const gr2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' };

function IndividualRegistrationForm() {
  const [formData, setFormData] = useState({
    display_name: '', full_name: '', email: '',
    mailing_street1: '', mailing_street2: '', mailing_city: '', mailing_state: '', mailing_zip: '', mailing_country: 'USA',
    year_established: '', city: '', state: '', geographic_description: '',
    bio: '', affiliated_group: '', event_types: '',
    website: '', facebook: '', instagram: '', tiktok: '', youtube: '', other_social: '',
    publish_full_name: false, publish_email: false, publish_mailing_address: false,
    comments: ''
  });
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) { setErrors(prev => ({ ...prev, photos: 'Maximum 3 photos allowed.' })); return; }
    const oversized = files.filter(f => f.size > 20 * 1024 * 1024);
    if (oversized.length > 0) { setErrors(prev => ({ ...prev, photos: 'One or more files exceed the 20MB size limit.' })); return; }
    setPhotos(files);
    setErrors(prev => ({ ...prev, photos: null }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.display_name.trim()) errs.display_name = 'Display name is required.';
    if (!formData.full_name.trim()) errs.full_name = 'Full name is required.';
    if (!formData.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email format.';
    if (!formData.mailing_street1.trim()) errs.mailing_street1 = 'Street address is required.';
    if (!formData.mailing_city.trim()) errs.mailing_city = 'City is required.';
    if (!formData.mailing_state) errs.mailing_state = 'State/Province is required.';
    if (!formData.mailing_zip.trim()) errs.mailing_zip = 'ZIP/Postal code is required.';
    if (!formData.city.trim()) errs.city = 'City is required.';
    if (!formData.state) errs.state = 'State/Province is required.';
    if (!formData.bio.trim()) errs.bio = 'Bio is required.';
    if (photos.length === 0) errs.photos = 'At least 1 photo is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { window.scrollTo(0, 0); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.keys(formData).forEach(key => fd.append(key, formData[key]));
      photos.forEach(photo => fd.append('photos', photo));
      const token = localStorage.getItem('token');
      await api.post('/api/individuals/register', fd, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (error) {
      alert('Submission failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const errStyle = { color: '#ff6b6b', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' };
  const fld = (label, name, type = 'input', required = false, placeholder = '') => (
    <div style={{ marginBottom: '1rem' }}>
      <label style={lbl}>{label}{required && ' *'}</label>
      {type === 'textarea'
        ? <textarea rows="5" style={inp} value={formData[name]} onChange={e => set(name, e.target.value)} placeholder={placeholder} />
        : <input type={type} style={{ ...inp, borderColor: errors[name] ? '#ff6b6b' : 'var(--border)' }} value={formData[name]} onChange={e => set(name, e.target.value)} placeholder={placeholder} />
      }
      {errors[name] && <span style={errStyle}>{errors[name]}</span>}
    </div>
  );

  if (success) {
    return (
      <div style={{ maxWidth: '600px', margin: '3rem auto', padding: '2rem', background: 'var(--dark-2)', border: '1px solid #2d6a2d', borderRadius: '4px', textAlign: 'center' }}>
        <h2 style={{ color: '#90ee90' }}>✓ Registration Submitted!</h2>
        <p>Thank you for registering as a Krampus performer with Krampus America!</p>
        <p style={{ color: 'var(--text-muted)' }}>Your registration has been submitted for review. Once approved, it will appear on the Krampus America website.</p>
        <p style={{ color: 'var(--text-muted)' }}>Check your <a href="/dashboard">Dashboard</a> for status updates.</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
          <a href="/dashboard" className="btn btn-primary">Go to Dashboard</a>
          <a href="/performers" className="btn btn-secondary">View Performers</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <h1>Performer Registration</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
        Register as an individual Krampus performer. All registrations are reviewed before being published.
        Fields marked with * are required.
      </p>

      {Object.keys(errors).length > 0 && (
        <div style={{ padding: '1rem', marginBottom: '1.5rem', background: 'rgba(139,0,0,0.2)', border: '1px solid var(--primary)', borderRadius: '4px', color: '#ff9999' }}>
          Please correct the errors below before submitting.
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* Private Info */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Private Information</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>This information is for Krampus America use only and will NOT be displayed publicly.</p>

          {fld('Full Name', 'full_name', 'input', true)}
          {fld('Email Address', 'email', 'email', true)}

          <h3 style={{ color: 'var(--accent)' }}>Mailing Address</h3>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Street Address 1 *</label>
            <input style={{ ...inp, borderColor: errors.mailing_street1 ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_street1} onChange={e => set('mailing_street1', e.target.value)} placeholder="123 Main St" />
            {errors.mailing_street1 && <span style={errStyle}>{errors.mailing_street1}</span>}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Street Address 2 (Optional)</label>
            <input style={inp} value={formData.mailing_street2} onChange={e => set('mailing_street2', e.target.value)} placeholder="Apt, Suite, Unit..." />
          </div>
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div>
              <label style={lbl}>City *</label>
              <input style={{ ...inp, borderColor: errors.mailing_city ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_city} onChange={e => set('mailing_city', e.target.value)} />
              {errors.mailing_city && <span style={errStyle}>{errors.mailing_city}</span>}
            </div>
            <div>
              <label style={lbl}>State / Province *</label>
              <select style={{ ...inp, borderColor: errors.mailing_state ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_state} onChange={e => set('mailing_state', e.target.value)}>
                <option value="">Select...</option>
                <optgroup label="United States">{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
                <optgroup label="Canada">{CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
              </select>
              {errors.mailing_state && <span style={errStyle}>{errors.mailing_state}</span>}
            </div>
          </div>
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div>
              <label style={lbl}>ZIP / Postal Code *</label>
              <input style={{ ...inp, borderColor: errors.mailing_zip ? '#ff6b6b' : 'var(--border)' }} value={formData.mailing_zip} onChange={e => set('mailing_zip', e.target.value)} />
              {errors.mailing_zip && <span style={errStyle}>{errors.mailing_zip}</span>}
            </div>
            <div>
              <label style={lbl}>Country</label>
              <select style={inp} value={formData.mailing_country} onChange={e => set('mailing_country', e.target.value)}>
                <option value="USA">USA</option>
                <option value="Canada">Canada</option>
              </select>
            </div>
          </div>
        </div>

        {/* Public Info */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Public Profile</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>This information will be displayed on the Krampus America website.</p>

          {fld('Display Name / Performer Name', 'display_name', 'input', true, 'The name shown publicly on the website')}
          {fld('Year Established', 'year_established', 'input', false, 'e.g. 2018')}

          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div>
              <label style={lbl}>City *</label>
              <input style={{ ...inp, borderColor: errors.city ? '#ff6b6b' : 'var(--border)' }} value={formData.city} onChange={e => set('city', e.target.value)} />
              {errors.city && <span style={errStyle}>{errors.city}</span>}
            </div>
            <div>
              <label style={lbl}>State / Province *</label>
              <select style={{ ...inp, borderColor: errors.state ? '#ff6b6b' : 'var(--border)' }} value={formData.state} onChange={e => set('state', e.target.value)}>
                <option value="">Select...</option>
                <optgroup label="United States">{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}</optgroup>
                <optgroup label="Canada">{CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
              </select>
              {errors.state && <span style={errStyle}>{errors.state}</span>}
            </div>
          </div>

          {fld('Geographic Description', 'geographic_description', 'input', false, 'e.g. Pacific Northwest, Greater Seattle Area')}

          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Bio *</label>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic', margin: '0 0 0.4rem 0' }}>Provide a publication-ready bio to be displayed on the website.</p>
            <textarea rows="6" style={{ ...inp, borderColor: errors.bio ? '#ff6b6b' : 'var(--border)' }} value={formData.bio} onChange={e => set('bio', e.target.value)} />
            {errors.bio && <span style={errStyle}>{errors.bio}</span>}
          </div>

          {fld('Affiliated Group', 'affiliated_group', 'input', false, 'Name of your primary Krampus group (if any)')}

          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Event Types</label>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic', margin: '0 0 0.4rem 0' }}>Describe the types of events you participate in (Krampusnacht, parades, private events, etc.)</p>
            <textarea rows="4" style={inp} value={formData.event_types} onChange={e => set('event_types', e.target.value)} />
          </div>
        </div>

        {/* Social Media */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Website & Social Media</h2>
          {fld('Website', 'website', 'url', false, 'https://')}
          <div style={{ ...gr2, marginBottom: '1rem' }}>
            <div><label style={lbl}>Facebook</label><input style={inp} value={formData.facebook} onChange={e => set('facebook', e.target.value)} placeholder="https://facebook.com/..." /></div>
            <div><label style={lbl}>Instagram</label><input style={inp} value={formData.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@username" /></div>
            <div><label style={lbl}>TikTok</label><input style={inp} value={formData.tiktok} onChange={e => set('tiktok', e.target.value)} placeholder="@username" /></div>
            <div><label style={lbl}>YouTube</label><input style={inp} value={formData.youtube} onChange={e => set('youtube', e.target.value)} placeholder="https://youtube.com/..." /></div>
          </div>
          {fld('Other Social Media', 'other_social', 'input', false, 'Any other social media links')}
        </div>

        {/* Privacy */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Privacy Settings</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Choose what contact information to display publicly on your profile.</p>
          {[['publish_full_name','Display my full name on the website'],['publish_email','Display my email on the website'],['publish_mailing_address','Display my mailing address on the website']].map(([field, label]) => (
            <div key={field} style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <input type="checkbox" checked={formData[field]} onChange={e => set(field, e.target.checked)} style={{ width: 'auto' }} />
                {label}
              </label>
            </div>
          ))}
        </div>

        {/* Photos */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Photos *</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>Upload 1–3 photos. Accepted format: JPG/JPEG. Maximum 20MB per photo.</p>
          <input type="file" accept=".jpg,.jpeg" multiple onChange={handlePhotoChange} style={{ color: 'var(--text)', marginBottom: '0.5rem' }} />
          {photos.length > 0 && <p style={{ color: '#90ee90', margin: '0.5rem 0 0' }}>{photos.length} photo(s) selected</p>}
          {errors.photos && <p style={{ color: '#ff6b6b', margin: '0.5rem 0 0' }}>{errors.photos}</p>}
        </div>

        {/* Comments */}
        <div style={section}>
          <h2 style={{ marginTop: 0 }}>Additional Comments</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Anything else you'd like Krampus America to know about your registration.</p>
          <textarea rows="4" style={inp} value={formData.comments} onChange={e => set('comments', e.target.value)} />
        </div>

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button type="submit" disabled={submitting} style={{ padding: '1rem 3rem', background: 'var(--primary)', color: 'white', border: '1px solid var(--primary-light)', borderRadius: '2px', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'Cinzel, serif', fontSize: '1rem', letterSpacing: '0.1em', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Submitting...' : 'Submit Registration'}
          </button>
        </div>

      </form>
    </div>
  );
}

export default IndividualRegistrationForm;
